import React from 'react';

import { Caption, Heading } from '@depop/web-thrift-ui/Typography';
import { ButtonLink } from '@depop/web-ui-kit/ButtonLink';
import { useTranslations } from 'next-intl';

import styles from './CondensedProductCard.module.css';

import { MobilePrice } from '@/app/[locale]/products/[slug]/components/Price/MobilePrice';
import { Placeholder, PlaceHolderShape } from '@/components/Placeholder';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';
import { useProductTracking } from '@/modules/product/hooks/useProductTracking';

type Props = {
  title: string;
  inlineAttributes: string[];
  extendedLoading: boolean;
  productBadges: React.ReactNode;
  onViewDetailsClickAction: () => void;
};

export function CondensedProductInfo({
  title,
  inlineAttributes,
  extendedLoading,
  productBadges,
  onViewDetailsClickAction,
}: Props) {
  const t = useTranslations('product');
  const { sendProductInteractionActionATEvent } = useProductTracking();
  const { data: productBasic } = useProductBySlugBasicPDP();

  function handleViewDetails() {
    sendProductInteractionActionATEvent(productBasic?.id, 'ViewDetails');
    onViewDetailsClickAction();
  }

  if (extendedLoading) {
    return (
      <div className={styles.productInfo}>
        <Heading as="h1" variant="md">
          {title}
        </Heading>
        <div className={styles.mobileAttributes}>
          <Placeholder
            shape={PlaceHolderShape.RECTANGLE}
            width="60%"
            height="16px"
          />
          <Placeholder
            shape={PlaceHolderShape.RECTANGLE}
            width="50%"
            height="16px"
          />
          <Placeholder
            shape={PlaceHolderShape.RECTANGLE}
            width="40%"
            height="16px"
          />
        </div>
        <div className={styles.desktopAttributes}>
          <Placeholder
            shape={PlaceHolderShape.RECTANGLE}
            width="60%"
            height="16px"
          />
        </div>
        <div className={styles.productInfoPrice}>
          <MobilePrice />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.productInfo}>
      <Heading as="h1" variant="md">
        {title}
      </Heading>

      {inlineAttributes.length > 0 && (
        <>
          <div className={styles.mobileAttributes}>
            {inlineAttributes.map((attr) => (
              <Caption key={attr} variant="lg-regular" colour="base">
                {attr}
              </Caption>
            ))}
          </div>
          <div className={styles.desktopAttributes}>
            <Caption variant="lg-regular" colour="base">
              {inlineAttributes.join(' · ')}
            </Caption>
          </div>
        </>
      )}

      <ButtonLink
        type="button"
        className={styles.viewDetailsButton}
        onClick={handleViewDetails}
      >
        <Caption colour="base" variant="lg-regular" as="span">
          {t('CondensedLayout.ViewDetails')}
        </Caption>
      </ButtonLink>

      <div className={styles.productInfoPrice}>
        <MobilePrice />
        {productBadges}
      </div>
    </div>
  );
}
